/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IImageSettings } from '@msdyn365-commerce/core';
import { Button, Modal, ModalBody } from '@msdyn365-commerce-modules/utilities';
import { CartLine, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import MsDyn365 from '@msdyn365-commerce/core';
import React from 'react';
import { Quantity } from '../../utilities/quantity';
import { PriceComponent } from './product-price-component';

export interface IDobbiesMiniCartSummaryProduct extends SimpleProduct {
    quantity?: number;
}

export interface IProductBundleSectionProps {
    // Display conditions
    allBundleProductsOOS: boolean;
    bundleHasProducts: boolean;
    bundleModalHasProducts: boolean;
    showProductBundlesInModal: boolean;
    isVirtualCartEmpty: boolean;
    isMaximumQuantityAdded: boolean;
    // Modal state
    renderProductBundlesModal: boolean;
    onCloseModal: () => void;
    // Slots
    bundledProductsSlot: React.ReactNode[] | undefined;
    bundledProductsModalSlot: React.ReactNode[] | undefined;
    // Cart data
    cartLines: CartLine[];
    cartProducts: IDobbiesMiniCartSummaryProduct[];
    // Config text
    productBundlesOrderSummaryText?: string;
    discountAppliedInCartMessage?: string;
    maxQuantityReachedInBundlesmsg?: string;
    totalPriceText?: string;
    // Actions
    onAddAllProductsToCart: () => void;
    onQuantityChange: (productId: number, newValue: number, maxQuantity: number) => void;
    onRemoveProduct: (productId: number, isSummaryInModal: boolean) => void;
    // Context
    context: Msdyn365.ICoreContext;
    // Scroll handling
    bundlesSummaryScrollPos: number;
    setBundlesSummaryScrollPos: React.Dispatch<React.SetStateAction<number>>;
    bundlesModalSummaryScrollPos: number;
    setBundlesModalSummaryScrollPos: React.Dispatch<React.SetStateAction<number>>;
    // Cookies for bundles
    productsAddedInBundles: any[];
}

const ProductBundleSection: React.FC<IProductBundleSectionProps> = ({
    allBundleProductsOOS,
    bundleHasProducts,
    bundleModalHasProducts,
    showProductBundlesInModal,
    isVirtualCartEmpty,
    isMaximumQuantityAdded,
    renderProductBundlesModal,
    onCloseModal,
    bundledProductsSlot,
    bundledProductsModalSlot,
    cartLines,
    cartProducts,
    productBundlesOrderSummaryText,
    discountAppliedInCartMessage,
    maxQuantityReachedInBundlesmsg,
    totalPriceText,
    onAddAllProductsToCart,
    onQuantityChange,
    onRemoveProduct,
    context,
    bundlesSummaryScrollPos,
    setBundlesSummaryScrollPos,
    bundlesModalSummaryScrollPos,
    setBundlesModalSummaryScrollPos,
    productsAddedInBundles
}) => {
    if (allBundleProductsOOS) {
        return null;
    }

    return (
        <>
            {/* Inline bundle section */}
            {bundleHasProducts && (
                <div className='product-bundle-wrapper'>
                    <div className='product-bundle'>
                        <h2 className='product-bundle__heading'>You might also like…</h2>
                        <div className='product-bundle-container'>
                            <div className='product-bundle-left'>{bundledProductsSlot && bundledProductsSlot[0]}</div>
                            {bundledProductsSlot && (
                                <div className='price-summary-wrapper'>
                                    <div className={`price-summary ${isVirtualCartEmpty ? 'empty' : ''}`}>
                                        <h3>{productBundlesOrderSummaryText || 'Price summary'}</h3>
                                        <ProductCartLines
                                            cartLines={cartLines}
                                            cartProducts={cartProducts}
                                            context={context}
                                            isSummaryInModal={false}
                                            productsAddedInBundles={productsAddedInBundles}
                                            maxQuantityReachedInBundlesmsg={maxQuantityReachedInBundlesmsg}
                                            totalPriceText={totalPriceText}
                                            onQuantityChange={onQuantityChange}
                                            onRemoveProduct={onRemoveProduct}
                                            bundlesSummaryScrollPos={bundlesSummaryScrollPos}
                                            setBundlesSummaryScrollPos={setBundlesSummaryScrollPos}
                                            bundlesModalSummaryScrollPos={bundlesModalSummaryScrollPos}
                                            setBundlesModalSummaryScrollPos={setBundlesModalSummaryScrollPos}
                                        />
                                        <Button
                                            className='msc-add-to-cart'
                                            disabled={isVirtualCartEmpty || isMaximumQuantityAdded}
                                            title='Buy All'
                                            onClick={onAddAllProductsToCart}
                                        >
                                            {isVirtualCartEmpty ? 'Add an item' : 'Add to basket'}
                                        </Button>
                                        <p>{discountAppliedInCartMessage || 'Any offers/discounts will be applied in basket'}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal bundle section */}
            {bundleModalHasProducts && showProductBundlesInModal && bundledProductsModalSlot && bundledProductsModalSlot[0] && (
                <Modal className='product-bundle-modal' isOpen={renderProductBundlesModal} toggle={onCloseModal}>
                    <ModalBody>
                        <div className='product-bundle-wrapper'>
                            <div className='product-bundle'>
                                <button
                                    type='button'
                                    className='close-modal'
                                    data-dismiss='modal'
                                    aria-label='Close'
                                    onClick={onCloseModal}
                                />
                                <h2 className='product-bundle__heading'>You might also like…</h2>
                                <div className='product-bundle-container'>
                                    <div className='product-bundle-left'>{bundledProductsModalSlot[0]}</div>
                                    <div className='price-summary-wrapper'>
                                        <div className={`price-summary ${!cartLines.length ? 'empty' : ''}`}>
                                            <h3>Your basket</h3>
                                            <ProductCartLines
                                                cartLines={cartLines}
                                                cartProducts={cartProducts}
                                                context={context}
                                                isSummaryInModal={true}
                                                productsAddedInBundles={productsAddedInBundles}
                                                maxQuantityReachedInBundlesmsg={maxQuantityReachedInBundlesmsg}
                                                totalPriceText={totalPriceText}
                                                onQuantityChange={onQuantityChange}
                                                onRemoveProduct={onRemoveProduct}
                                                bundlesSummaryScrollPos={bundlesSummaryScrollPos}
                                                setBundlesSummaryScrollPos={setBundlesSummaryScrollPos}
                                                bundlesModalSummaryScrollPos={bundlesModalSummaryScrollPos}
                                                setBundlesModalSummaryScrollPos={setBundlesModalSummaryScrollPos}
                                            />
                                            <Button
                                                className='msc-add-to-cart'
                                                disabled={!cartLines.length}
                                                title='Buy All'
                                                onClick={() => {
                                                    MsDyn365.isBrowser && window && window.location.assign('/cart');
                                                }}
                                            >
                                                {!cartLines.length ? 'Add an item' : 'View your basket'}
                                            </Button>
                                            <p>{discountAppliedInCartMessage || 'Any offers/discounts will be applied at checkout'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>
            )}
        </>
    );
};

// Sub-component for rendering cart lines
interface IProductCartLinesProps {
    cartLines: CartLine[];
    cartProducts: IDobbiesMiniCartSummaryProduct[];
    context: Msdyn365.ICoreContext;
    isSummaryInModal: boolean;
    productsAddedInBundles: any[];
    maxQuantityReachedInBundlesmsg?: string;
    totalPriceText?: string;
    onQuantityChange: (productId: number, newValue: number, maxQuantity: number) => void;
    onRemoveProduct: (productId: number, isSummaryInModal: boolean) => void;
    bundlesSummaryScrollPos: number;
    setBundlesSummaryScrollPos: React.Dispatch<React.SetStateAction<number>>;
    bundlesModalSummaryScrollPos: number;
    setBundlesModalSummaryScrollPos: React.Dispatch<React.SetStateAction<number>>;
}

const ProductCartLines: React.FC<IProductCartLinesProps> = ({
    cartLines,
    cartProducts,
    context,
    isSummaryInModal,
    productsAddedInBundles,
    maxQuantityReachedInBundlesmsg,
    totalPriceText,
    onQuantityChange,
    onRemoveProduct,
    bundlesSummaryScrollPos,
    setBundlesSummaryScrollPos,
    bundlesModalSummaryScrollPos,
    setBundlesModalSummaryScrollPos
}) => {
    let totalPrice = 0.0;
    const elemId = isSummaryInModal ? 'product_bundles_summary_modal' : 'product_bundles_summary';
    const elem = MsDyn365.isBrowser && window && window.document.getElementById(elemId);
    const rowHeight = 116;
    const minProductsToScroll =
        isSummaryInModal ||
        (MsDyn365.isBrowser && window && window.innerWidth >= 1280 && MsDyn365.isBrowser && window && window.innerWidth <= 1476)
            ? 2
            : 3;
    const upArrowId = isSummaryInModal ? 'up-arrow-btn-modal' : 'up-arrow-btn';
    const downArrowId = isSummaryInModal ? 'down-arrow-btn-modal' : 'down-arrow-btn';
    let scrollPos = isSummaryInModal ? bundlesModalSummaryScrollPos : bundlesSummaryScrollPos;
    const rowHeightForCalc = 90;
    const defaultMaxQuantityMsg = `There is only 1 product left. Please choose quantity within the available range`;

    // Filter cart products based on context
    let filteredCartProducts = [...cartProducts];
    if (isSummaryInModal) {
        filteredCartProducts = filteredCartProducts.filter(cp => (cartLines.find(cl => cl.ProductId === cp.RecordId) ? true : false));
        if (filteredCartProducts.length && cartLines.length) {
            for (const c of cartLines) {
                for (const cp of filteredCartProducts) {
                    if (c.ProductId === cp.RecordId) {
                        cp.quantity = c.Quantity;
                    }
                }
            }
        }
    } else {
        filteredCartProducts = filteredCartProducts.filter(
            cp => Array.isArray(productsAddedInBundles) && productsAddedInBundles.find(p => p['productId'] === cp.RecordId)
        );
    }

    const handleScroll = () => {
        if (elem && Math.abs(Math.ceil(elem.scrollTop) - Math.ceil(scrollPos)) > rowHeightForCalc) {
            if (!isSummaryInModal) {
                if (elem.scrollTop >= (filteredCartProducts.length - (minProductsToScroll + 1)) * rowHeightForCalc) {
                    setBundlesSummaryScrollPos(elem.scrollTop);
                } else if (elem.scrollTop < 50) {
                    setBundlesSummaryScrollPos(elem.scrollTop);
                }
            } else {
                if (elem.scrollTop >= (filteredCartProducts.length - minProductsToScroll + 1) * rowHeightForCalc) {
                    setBundlesModalSummaryScrollPos(elem.scrollTop);
                } else if (elem.scrollTop < 50) {
                    setBundlesModalSummaryScrollPos(elem.scrollTop);
                }
                const upArrow = MsDyn365.isBrowser && window && window.document.getElementById(upArrowId);
                const downArrow = MsDyn365.isBrowser && window && window.document.getElementById(downArrowId);

                if (elem.scrollTop < 50) {
                    if (upArrow) upArrow.hidden = true;
                } else {
                    if (upArrow) upArrow.hidden = false;
                }
                if (elem.scrollTop > (filteredCartProducts.length - minProductsToScroll) * rowHeightForCalc) {
                    if (downArrow) downArrow.hidden = true;
                } else {
                    if (downArrow) downArrow.hidden = false;
                }
            }
            scrollPos = elem.scrollTop;
        }
    };

    const baseImageUrl: string = context.request.apiSettings.baseImageUrl;
    const imageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=80&h=94&m=6', w: 0, h: 0 },
            sm: { q: 'w=148&h=174&m=6', w: 0, h: 0 },
            lg: { q: 'w=148&h=174&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };

    return (
        <>
            {filteredCartProducts.length > minProductsToScroll && (
                <Button
                    id={upArrowId}
                    onClick={() => {
                        if (elem) {
                            elem.scrollTop = elem.scrollTop - rowHeight > 0 ? elem.scrollTop - rowHeight : 0;
                            if (isSummaryInModal) {
                                setBundlesModalSummaryScrollPos(elem.scrollTop);
                            } else {
                                setBundlesSummaryScrollPos(elem.scrollTop);
                            }
                        }
                    }}
                    hidden={elem && scrollPos < 50 ? true : false}
                />
            )}

            <div id={elemId} className='price-summary__product-list' onScroll={handleScroll}>
                {filteredCartProducts.map(product => {
                    let prodInBundles = undefined;
                    let cookiePrice = undefined;
                    let maxQuantityForBundle = 0;
                    let shouldShowQuantityReachedErrorMsg = false;
                    const fallbackImageUrl =
                        product && getFallbackImageUrl(product.ItemId, context.actionContext.requestContext.apiSettings);
                    const cl = cartLines.find(c => c.ProductId === product.RecordId);

                    if (Array.isArray(productsAddedInBundles)) {
                        prodInBundles = productsAddedInBundles.find(p => p['productId'] === product.RecordId);
                        if (prodInBundles) {
                            cookiePrice = prodInBundles['price'];
                            maxQuantityForBundle = prodInBundles['maxQuantity'];
                            if (!isSummaryInModal) {
                                product.quantity = prodInBundles['quantity'];
                                if (cl && cl.Quantity && product.quantity && maxQuantityForBundle) {
                                    shouldShowQuantityReachedErrorMsg = product.quantity + cl.Quantity > maxQuantityForBundle;
                                }
                            }
                        }
                    }

                    if (maxQuantityForBundle <= 0) {
                        const cookies = context.actionContext.requestContext.cookies;
                        const maxQuantities = cookies.get('product_bundles_maximum_available_quantities')?.value || [];
                        if (Array.isArray(maxQuantities)) {
                            for (const maxQ of maxQuantities) {
                                if (maxQ.productId === product.RecordId) {
                                    maxQuantityForBundle = maxQ.maxQuantity;
                                    break;
                                }
                            }
                        }
                    }

                    if (product.quantity && product.quantity >= maxQuantityForBundle) {
                        shouldShowQuantityReachedErrorMsg = true;
                    }

                    const priceObj: ProductPrice = {
                        BasePrice: product.BasePrice,
                        AdjustedPrice: cookiePrice ? cookiePrice : product.Price,
                        CustomerContextualPrice: cookiePrice ? cookiePrice : product.Price
                    };

                    totalPrice += (cookiePrice ? Number(cookiePrice) : product.Price) * (product.quantity || 0);

                    return (
                        <div key={product.RecordId} className='position-relative price-summary__product'>
                            <div className='price-summary__content'>
                                <div className='msc-cart-line__product-image'>
                                    <Msdyn365.Image
                                        src={`${baseImageUrl}${product.PrimaryImageUrl}` || ''}
                                        fallBackSrc={fallbackImageUrl}
                                        altText={product.Name}
                                        gridSettings={context.request.gridSettings!}
                                        imageSettings={imageSettings}
                                        loadFailureBehavior='empty'
                                    />
                                </div>
                                <div>
                                    <p className='product-name' title={product.Name}>
                                        {product.Name}
                                    </p>
                                    <PriceComponent
                                        context={context}
                                        id={'dobbies-product-collection'}
                                        typeName={'dobbies-product-collection'}
                                        data={{ price: priceObj }}
                                        savingsText={undefined}
                                        freePriceText={'Free'}
                                        originalPriceText={'Original price'}
                                        discountLine={undefined}
                                        cartData={undefined}
                                        updatedPrice={cookiePrice ? cookiePrice : product.Price}
                                        getTierId={undefined}
                                        clubPlusDiscountsOfferId={undefined}
                                    />
                                    <p className='product-quantity'>Quantity: </p>
                                    <Quantity
                                        id={`msc-cart-line__quantity_${product.RecordId}`}
                                        currentCount={product.quantity}
                                        onChange={(newVal: number) => onQuantityChange(product.RecordId, newVal, maxQuantityForBundle)}
                                        hideQuantityControls={false}
                                        disabledInput={false}
                                        max={maxQuantityForBundle}
                                        min={1}
                                    />
                                    {shouldShowQuantityReachedErrorMsg && (
                                        <p className='quantity-error-msg'>{maxQuantityReachedInBundlesmsg || defaultMaxQuantityMsg}</p>
                                    )}
                                </div>
                            </div>
                            <Button className='product-remove' onClick={() => onRemoveProduct(product.RecordId, isSummaryInModal)} />
                        </div>
                    );
                })}
            </div>

            {filteredCartProducts.length > minProductsToScroll && (
                <Button
                    id={downArrowId}
                    onClick={() => {
                        if (elem) {
                            elem.scrollTop += rowHeight;
                            if (isSummaryInModal) {
                                setBundlesModalSummaryScrollPos(elem.scrollTop);
                            } else {
                                setBundlesSummaryScrollPos(elem.scrollTop);
                            }
                        }
                    }}
                    hidden={elem && scrollPos > (filteredCartProducts.length - minProductsToScroll) * rowHeightForCalc ? true : false}
                />
            )}

            <p className='price-summary__total-price'>
                {totalPriceText || 'Total price:'}
                <span>
                    {' '}
                    {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currencyDisplay: 'symbol',
                        currency: 'GBP',
                        minimumFractionDigits: 2
                    }).format(totalPrice)}
                </span>
            </p>
        </>
    );
};

export default ProductBundleSection;
export { ProductCartLines };
